import * as Yup from 'yup';

/**
 * Validation schema for location preferences
 * 
 * Provides validation rules for each preference field to ensure data integrity
 * and proper formatting. These validations help prevent invalid data from
 * being saved to the database.
 */

// Regular expressions for validation
const PHONE_FORMAT_REGEX = /^[()\\-\\sX\\d]+$/; // Allows digits, X, parentheses, dashes, spaces
const COUNTRY_CODE_REGEX = /^\\+\\d{1,4}$/; // Plus sign followed by 1-4 digits
const POSTAL_CODE_FORMAT_REGEX = /^[A-Za-z0-9\\s-]+$/; // Letters, numbers, spaces, dashes
const NUMBER_FORMAT_REGEX = /^[A-Za-z0-9-{}]+$/; // Letters, numbers, dashes, curly braces

// Valid timezone values (subset - you can expand this list)
const VALID_TIMEZONES = [
    'America/Toronto', 'America/Chicago', 'America/Denver', 
    'America/Los_Angeles', 'America/New_York', 'America/Vancouver', 'America/Halifax',
    'Europe/London', 'Europe/Paris', 'Asia/Tokyo'
];

// Valid currency codes (ISO 4217)
const VALID_CURRENCIES = ['CAD', 'USD', 'EUR', 'GBP', 'JPY', 'AUD'];

// Valid date formats
const VALID_DATE_FORMATS = [
    'YYYY-MM-DD', 'MM/DD/YYYY', 'DD/MM/YYYY', 
    'DD-MM-YYYY', 'YYYY/MM/DD', 'MM-DD-YYYY'
];

/**
 * Location preferences validation schema
 * 
 * Each field is optional since preferences can inherit from business defaults
 * When provided, values must meet format requirements
 */
export const locationPreferencesSchema = Yup.object().shape({
    preferences: Yup.object().shape({
        // Phone number format validation
        phoneFormat: Yup.string()
            .matches(PHONE_FORMAT_REGEX, 'Phone format must contain only digits, X, parentheses, dashes, and spaces')
            .max(20, 'Phone format must be 20 characters or less')
            .optional(),

        // Country code validation
        countryCode: Yup.string()
            .matches(COUNTRY_CODE_REGEX, 'Country code must start with + followed by 1-4 digits')
            .min(2, 'Country code must be at least 2 characters')
            .max(5, 'Country code must be 5 characters or less')
            .optional(),

        // Timezone validation
        timezone: Yup.string()
            .oneOf(VALID_TIMEZONES, 'Please select a valid timezone')
            .optional(),

        // Currency validation
        currency: Yup.string()
            .oneOf(VALID_CURRENCIES, 'Please select a valid currency code')
            .length(3, 'Currency code must be exactly 3 characters')
            .optional(),

        // Date format validation
        dateFormat: Yup.string()
            .oneOf(VALID_DATE_FORMATS, 'Please select a valid date format')
            .optional(),

        // Postal code format validation
        postalCodeFormat: Yup.string()
            .matches(POSTAL_CODE_FORMAT_REGEX, 'Postal code format contains invalid characters')
            .max(15, 'Postal code format must be 15 characters or less')
            .optional(),

        // Invoice number format validation
        invoiceFormat: Yup.string()
            .matches(NUMBER_FORMAT_REGEX, 'Invoice format must contain only letters, numbers, dashes, and {braces}')
            .max(50, 'Invoice format must be 50 characters or less')
            .test('has-placeholders', 'Invoice format should include placeholders like {YYYY} or {####}', 
                value => !value || value.includes('{'))
            .optional(),

        // Prescription number format validation
        prescriptionFormat: Yup.string()
            .matches(NUMBER_FORMAT_REGEX, 'Prescription format must contain only letters, numbers, dashes, and {braces}')
            .max(50, 'Prescription format must be 50 characters or less')
            .test('has-placeholders', 'Prescription format should include placeholders like {YYYY} or {####}', 
                value => !value || value.includes('{'))
            .optional(),

        lowStockQuantityThreshold: Yup.number()
            .typeError('Low stock threshold must be a number')
            .min(0, 'Low stock threshold cannot be negative')
            .optional()
    }).optional()
});

/**
 * Extended location validation schema that includes both location data and preferences
 * This combines the existing location validation with preference validation
 */
export const extendedLocationSchema = Yup.object().shape({
    // Basic location fields (you'll need to import your existing location schema)
    name: Yup.string().required('Location name is required'),
    storePrefix: Yup.string()
        .transform((value) => value === '' ? null : value)
        .nullable()
        .max(10, 'Store prefix must be 10 characters or less')
        .matches(/^[A-Za-z0-9\s-]*$/, 'Store prefix can only contain letters, numbers, spaces, and dashes'),
    addrNo: Yup.string().required('Address number is required'),
    addrStreet: Yup.string().required('Street address is required'),
    addrCity: Yup.string().required('City is required'),
    addrProvince: Yup.string().required('Province is required'),
    addrPostal: Yup.string().required('Postal code is required'),
    addrCountry: Yup.string()
        .oneOf(['Canada', 'USA'], 'Country must be Canada or United States')
        .required('Country is required'),
    phone: Yup.string().required('Phone number is required'),
    timezone: Yup.string().required('Timezone is required'),
    
    // Include preferences validation
    ...locationPreferencesSchema.fields
});

/**
 * Validation helper functions
 */
export const validatePreferenceValue = (fieldName, value) => {
    const fieldSchema = locationPreferencesSchema.fields.preferences.fields[fieldName];
    
    try {
        fieldSchema.validateSync(value);
        return { isValid: true, error: null };
    } catch (error) {
        return { isValid: false, error: error.message };
    }
};

/**
 * Format validation error messages for display
 */
export const formatValidationErrors = (errors) => {
    if (!errors || !errors.preferences) return {};
    
    const formattedErrors = {};
    Object.keys(errors.preferences).forEach(key => {
        formattedErrors[key] = errors.preferences[key];
    });
    
    return formattedErrors;
};

export default locationPreferencesSchema;
